import { useEffect, useState } from 'react';
import { Input, InputNumber } from 'antd';
import { ICurrencySearchItem } from 'types/currency.types';
import { CountryDefaultCurrencyRole, ICountryDetail, IUpdateCountry } from 'types/countries';
import { BordSwitch, BordTooltip } from 'bord-design-system';
import { BlackCheck } from '../';
import CountryCurrencyDropdown from '../countryCurrencyDropdown/CountryCurrencyDropdown';
import CountryProviderCell from '../countryProviderCell/CountryProviderCell';
import LoaderRow from '../loaderRow/LoaderRow';
import {
  buildCountryRowFieldHasValue,
  buildCountryRowFormState,
  countryPhoneCodeInputNumberProps,
  countryTaxRateInputNumberProps,
  getCountryRowFilledClassName,
  isCountryRowFormInvalid,
  normalizeDocumentExample,
  parseCatalogExchangeRate
} from '../../countriesTab.utils';

import './countryRow.scss';

interface ICountryRow {
  countryData: ICountryDetail;
  handleupdateCountry: (data: IUpdateCountry, countryId: number) => void;
  handleNewProvider: (provider: string, countryCode: string, countryId: number) => void;
  updateLoading?: number;
}

/**
 * Fila editable de la tabla de países con sus campos de configuración.
 */
const CountryRow = ({ countryData, handleupdateCountry, handleNewProvider, updateLoading }: ICountryRow) => {
  const {
    id: countryId,
    code: countryCode,
    flag: countryFlagUrl,
    name: countryName,
    currencyId: primaryCurrencyId,
    currencyCode: primaryCurrencyCode,
    secondaryCurrencyCode,
    defaultCurrencyRole: countryDefaultCurrencyRole,
    exchangeRate: catalogExchangeRateValue,
    documentTypeId,
    documentType,
    providers: countryProviders = []
  } = countryData;

  const [updateCountry, setUpdateCountry] = useState<IUpdateCountry>();
  const [pendingPrimaryCurrency, setPendingPrimaryCurrency] = useState<ICurrencySearchItem | null>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const isCountryRowLoading = updateLoading === countryId;
  const catalogExchangeRate = parseCatalogExchangeRate(catalogExchangeRateValue);

  const getDisplayExchangeRate = (): number | undefined => {
    if (pendingPrimaryCurrency != null && pendingPrimaryCurrency.id !== primaryCurrencyId) {
      return pendingPrimaryCurrency.exchangeRate ?? undefined;
    }

    return catalogExchangeRate;
  };

  const handleCurrencySelect = (currency: ICurrencySearchItem) => {
    if (!countryId || currency.id === primaryCurrencyId) return;

    setPendingPrimaryCurrency(currency);
    setIsEdit(true);
  };

  const displayPrimaryCurrencyCode = pendingPrimaryCurrency?.code ?? primaryCurrencyCode;
  const displayPrimaryCurrencyId = pendingPrimaryCurrency?.id ?? primaryCurrencyId;
  const displayPrimaryCurrencyLabel = displayPrimaryCurrencyCode?.toUpperCase() ?? '';
  const hasPendingPrimaryCurrencyChange =
    pendingPrimaryCurrency != null && pendingPrimaryCurrency.id !== primaryCurrencyId;
  const hasSecondaryCurrency = hasPendingPrimaryCurrencyChange
    ? displayPrimaryCurrencyLabel !== 'USD'
    : !!secondaryCurrencyCode;
  const currentDefaultCurrencyRole =
    updateCountry?.defaultCurrencyRole ?? countryDefaultCurrencyRole ?? CountryDefaultCurrencyRole.PRIMARY;
  const isSecondaryDefault = currentDefaultCurrencyRole === CountryDefaultCurrencyRole.SECONDARY;
  const secondaryCurrencyLabel = secondaryCurrencyCode?.toUpperCase() ?? 'USD';
  const isUsdOnlyCountry = displayPrimaryCurrencyLabel === 'USD' && !hasSecondaryCurrency;
  const switchOffLabel = isUsdOnlyCountry ? '' : displayPrimaryCurrencyLabel;
  const isCurrencySwitchDisabled = !hasSecondaryCurrency;
  const isCurrencySwitchChecked = hasSecondaryCurrency ? isSecondaryDefault : true;

  const handleDefaultCurrencySwitch = (isSecondarySelected: boolean) => {
    if (isCurrencySwitchDisabled || !updateCountry) return;

    setIsEdit(true);
    setUpdateCountry({
      ...updateCountry,
      defaultCurrencyRole: isSecondarySelected
        ? CountryDefaultCurrencyRole.SECONDARY
        : CountryDefaultCurrencyRole.PRIMARY
    });
  };

  const saveData = () => {
    if (!countryId || !updateCountry) return;

    const displayExchangeRate = getDisplayExchangeRate();

    handleupdateCountry(
      {
        currencyId: displayPrimaryCurrencyId,
        defaultCurrencyRole: updateCountry.defaultCurrencyRole,
        exchangeRate: displayExchangeRate,
        taxRate: updateCountry.taxRate,
        phoneCode: updateCountry.phoneCode,
        documentTypeId,
        documentExample: updateCountry.documentExample,
        registry: updateCountry.registry,
        buy: updateCountry.buy,
        logisticService: updateCountry.logisticService,
        storage: updateCountry.storage
      },
      countryId
    );
    setPendingPrimaryCurrency(null);
    setIsEdit(false);
  };

  const handleDocumentChange = (example: string) => {
    if (!updateCountry) return;

    setIsEdit(true);
    setUpdateCountry({ ...updateCountry, documentExample: normalizeDocumentExample(example) });
  };

  const displayExchangeRate = getDisplayExchangeRate();
  const fieldHasValue = buildCountryRowFieldHasValue(updateCountry, displayExchangeRate, countryProviders.length);
  const isSaveDisabled = !isEdit || isCountryRowFormInvalid(updateCountry, displayExchangeRate);

  useEffect(() => {
    setUpdateCountry(buildCountryRowFormState(countryData));
    setPendingPrimaryCurrency(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryData]);

  if (!updateCountry || isCountryRowLoading) {
    return <LoaderRow />;
  }

  return (
    <tr className="countryTableRow">
      <td className="countryFlagColumn">
        <div className="flagWrapper">
          <div className="flagBox" style={{ backgroundImage: `url(${countryFlagUrl})` }} />
          <div className="countryNameHover">{countryName || 'Sin información del nombre'}</div>
        </div>
      </td>

      <td className="countryCurrencyColumn">
        <CountryCurrencyDropdown
          countryId={countryId}
          selectedCurrencyCode={displayPrimaryCurrencyCode}
          onCurrencySelect={handleCurrencySelect}
        />
      </td>

      <td className="exchangeRateColumn">
        <span className="labelCountryInput filledLabel">Tasa de cambio</span>
        <BordTooltip
          message="Actualiza la tasa de cambio en la pestaña Monedas."
          position="bottom"
          customStylesMessageContainer={{ maxWidth: '20rem' }}
          followMouse
        >
          <InputNumber
            placeholder={displayPrimaryCurrencyCode}
            value={displayExchangeRate}
            disabled
            className="countryNumberInput readOnlyExchangeRate"
          />
        </BordTooltip>
      </td>

      <td className="taxesColumn">
        <span className={getCountryRowFilledClassName('labelCountryInput', fieldHasValue.taxes)}>Impuestos</span>
        <InputNumber
          defaultValue={updateCountry.taxRate}
          onChange={taxRateValue => {
            setIsEdit(true);
            setUpdateCountry({ ...updateCountry, taxRate: taxRateValue });
          }}
          {...countryTaxRateInputNumberProps}
          className={getCountryRowFilledClassName('countryNumberInput', fieldHasValue.taxes, 'filledInput')}
        />
      </td>

      <td className="phoneCodeColumn">
        <span className={getCountryRowFilledClassName('labelCountryInput', fieldHasValue.phoneCode)}>Indicativo</span>
        <InputNumber
          defaultValue={updateCountry.phoneCode}
          onChange={phoneCodeValue => {
            setIsEdit(true);
            setUpdateCountry({ ...updateCountry, phoneCode: phoneCodeValue });
          }}
          {...countryPhoneCodeInputNumberProps}
          className={getCountryRowFilledClassName('countryNumberInput', fieldHasValue.phoneCode, 'filledInput')}
        />
      </td>

      <td className="documentColumn">
        <span className={getCountryRowFilledClassName('labelCountryInput uppercase', fieldHasValue.document)}>
          {documentType}
        </span>
        <Input
          defaultValue={updateCountry.documentExample}
          onChange={event => handleDocumentChange(event.target.value)}
          placeholder={documentType || ''}
          className={getCountryRowFilledClassName('countrySimpleInput', fieldHasValue.document, 'filledInput')}
          value={updateCountry.documentExample}
        />
      </td>

      <td className="providerColumn">
        <CountryProviderCell
          countryId={countryId}
          countryCode={countryCode}
          countryProviders={countryProviders}
          hasProviderValue={fieldHasValue.provider}
          onAddProvider={handleNewProvider}
        />
      </td>

      <td className="registryColumn">
        <div className="countryRowInputZone">
          <BlackCheck
            handleChangeCheck={() => {
              setIsEdit(true);
              setUpdateCountry({ ...updateCountry, registry: !updateCountry.registry });
            }}
            checked={!!updateCountry.registry}
          />
        </div>
      </td>

      <td className="buyColumn">
        <div className="countryRowInputZone">
          <BlackCheck
            handleChangeCheck={() => {
              setIsEdit(true);
              setUpdateCountry({ ...updateCountry, buy: !updateCountry.buy });
            }}
            checked={!!updateCountry.buy}
          />
        </div>
      </td>

      <td className="servicesColumn">
        <div className="countryRowInputZone">
          <BlackCheck
            handleChangeCheck={() => {
              setIsEdit(true);
              setUpdateCountry({ ...updateCountry, logisticService: !updateCountry.logisticService });
            }}
            checked={!!updateCountry.logisticService}
          />
        </div>
      </td>

      <td className="storageColumn">
        <div className="countryRowInputZone">
          <BlackCheck
            handleChangeCheck={() => {
              setIsEdit(true);
              setUpdateCountry({ ...updateCountry, storage: !updateCountry.storage });
            }}
            checked={!!updateCountry.storage}
          />
        </div>
      </td>

      <td className="costsCurrencyColumn">
        <div className="countryRowInputZone">
          <BordSwitch
            variant="segmented"
            checked={isCurrencySwitchChecked}
            onLabel={secondaryCurrencyLabel}
            offLabel={switchOffLabel}
            disabled={isCurrencySwitchDisabled}
            onChange={handleDefaultCurrencySwitch}
          />
        </div>
      </td>

      <td className="actionsColumn">
        <div className="countryRowInputZone">
          <button className="saveButton" type="button" onClick={saveData} disabled={isSaveDisabled}>
            Guardar
          </button>
        </div>
      </td>
    </tr>
  );
};

export default CountryRow;
